import { useCallback, useEffect, useRef, useState } from "react";
import type { PublicContractEmailChangeResolveResponseDto } from "@madamuh/domain";
import {
  confirmPublicContractEmailChangeCurrent,
  confirmPublicContractEmailChangeNew,
  resolvePublicContractEmailChange
} from "../../shared/api/commercialContract";

function describeStage(stage: PublicContractEmailChangeResolveResponseDto["stage"]): string {
  switch (stage) {
    case "currentEmailApproval":
      return "現在の契約メールでの承認";
    case "newEmailVerification":
      return "新しい契約メールの確認";
    case null:
    default:
      return "確認済みまたは利用できないリンク";
  }
}

function describeStatus(status: PublicContractEmailChangeResolveResponseDto["status"]): string {
  switch (status) {
    case "valid":
      return "確認できます";
    case "alreadyUsed":
      return "このリンクはすでに使用済みです";
    case "expired":
      return "このリンクは期限切れです";
    case "revoked":
      return "このリンクは無効化されています";
    case "invalid":
    default:
      return "このリンクは利用できません";
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
}

function readTokenFromLocation(): string {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const hashToken = hashParams.get("token")?.trim();
  if (hashToken) {
    return hashToken;
  }

  return new URLSearchParams(window.location.search).get("token")?.trim() ?? "";
}

function removeTokenFromBrowserUrl() {
  if (!window.location.search && !window.location.hash) {
    return;
  }
  window.history.replaceState(null, document.title, window.location.pathname);
}

export function ContractEmailChangeActionPage() {
  const [token, setToken] = useState("");
  const [resolution, setResolution] = useState<PublicContractEmailChangeResolveResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [confirmState, setConfirmState] = useState<"idle" | "submitting">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const resolveSequenceRef = useRef(0);

  const processLocationToken = useCallback((input: { allowEmpty: boolean }) => {
    const nextToken = readTokenFromLocation();
    removeTokenFromBrowserUrl();

    if (!nextToken && !input.allowEmpty) {
      return;
    }

    const sequence = resolveSequenceRef.current + 1;
    resolveSequenceRef.current = sequence;
    setToken(nextToken);
    setResolution(null);
    setLoadState("idle");
    setConfirmState("idle");
    setMessage(null);

    if (!nextToken) {
      setMessage("確認リンクに必要な情報がありません。");
      return;
    }

    async function resolve() {
      setLoadState("loading");
      setMessage(null);
      try {
        const nextResolution = await resolvePublicContractEmailChange(nextToken);
        if (resolveSequenceRef.current !== sequence) {
          return;
        }
        setResolution(nextResolution);
        setLoadState("idle");
      } catch {
        if (resolveSequenceRef.current !== sequence) {
          return;
        }
        setLoadState("error");
        setMessage("確認リンクを読み込めませんでした。リンクの有効期限をご確認ください。");
      }
    }

    void resolve();
  }, []);

  useEffect(() => {
    processLocationToken({ allowEmpty: true });
  }, [processLocationToken]);

  useEffect(() => {
    function handleHashChange() {
      processLocationToken({ allowEmpty: false });
    }

    function handlePageShow() {
      processLocationToken({ allowEmpty: false });
    }

    window.addEventListener("hashchange", handleHashChange);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [processLocationToken]);

  async function handleConfirm() {
    if (!token || !resolution?.stage) {
      return;
    }

    setConfirmState("submitting");
    setMessage(null);

    try {
      if (resolution.stage === "currentEmailApproval") {
        await confirmPublicContractEmailChangeCurrent(token);
        setMessage("現在の契約メールで承認しました。新しいメールアドレスへ確認リンクを送信します。");
      } else {
        await confirmPublicContractEmailChangeNew(token);
        setMessage(
          "契約連絡先メールアドレスの変更が完了しました。管理画面を開いている場合は、画面へ戻ると最新の情報が表示されます。"
        );
      }
      const nextResolution = await resolvePublicContractEmailChange(token);
      setResolution(nextResolution);
    } catch {
      setMessage("確認処理に失敗しました。リンクが期限切れ、またはすでに使用済みの可能性があります。");
    } finally {
      setConfirmState("idle");
    }
  }

  const canConfirm = resolution?.status === "valid" && Boolean(resolution.stage);

  return (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: 16 }}>
      <section style={{ display: "grid", gap: 16, padding: 20, border: "1px solid #e5e7eb", borderRadius: 16 }}>
        <div>
          <p style={{ margin: "0 0 6px", color: "#6b7280", fontSize: 13 }}>MADAMUH LESSON</p>
          <h1 style={{ margin: 0 }}>契約メール変更の確認</h1>
        </div>

        {loadState === "loading" ? <p style={{ margin: 0 }}>確認リンクを読み込んでいます。</p> : null}
        {loadState === "error" || message ? <p style={{ margin: 0 }}>{message}</p> : null}

        {resolution ? (
          <div style={{ display: "grid", gap: 10 }}>
            <p style={{ margin: 0, fontWeight: 700 }}>{describeStatus(resolution.status)}</p>
            <dl style={{ margin: 0, display: "grid", gap: 8 }}>
              <div><dt>クラブ</dt><dd>{resolution.tenantDisplayName ?? "-"}</dd></div>
              <div><dt>手続き</dt><dd>{describeStage(resolution.stage)}</dd></div>
              <div><dt>現在の契約メール</dt><dd>{resolution.currentEmailMasked ?? "-"}</dd></div>
              <div><dt>新しい契約メール</dt><dd>{resolution.newEmailMasked ?? "-"}</dd></div>
              <div><dt>期限</dt><dd>{formatDate(resolution.expiresAt)}</dd></div>
            </dl>
            <button type="button" disabled={!canConfirm || confirmState === "submitting"} onClick={() => void handleConfirm()}>
              {confirmState === "submitting" ? "確認中..." : "この内容で確認する"}
            </button>
            <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
              この画面はリンクの内容確認だけでは変更しません。上のボタンを押したときだけ手続きが進みます。
            </p>
          </div>
        ) : null}
      </section>
    </main>
  );
}
